import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { client } from "../sanity";
import SEO from "../components/SEO";

const POSTS_PER_PAGE = 9;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateOrder, setDateOrder] = useState("newest");
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setIsLoading(true);
    setErrorMessage("");

    client
      .fetch(`*[_type == "post"] | order(coalesce(publishedAt, _createdAt) desc){
        title,
        slug,
        publishedAt,
        "date": coalesce(publishedAt, _createdAt),
        mainImage{
          asset->{url}
        },
        body,
        author->{
          name
        },
        categories[]->{
          title
        }
      }`)
      .then((data) => {
        setPosts(data);
      })
      .catch((error) => {
        console.error(error);
        setErrorMessage(
          error?.message ||
            "We could not load blog posts right now. Please confirm that your Sanity posts are published in the production dataset and that the dataset is publicly readable."
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const getExcerpt = (body) => {
    if (!body || !Array.isArray(body)) {
      return "Discover insights, updates, and ideas from Jisedai.";
    }

    const firstBlock = body.find((block) => block._type === "block");
    if (!firstBlock?.children) {
      return "Discover insights, updates, and ideas from Jisedai.";
    }

    const text = firstBlock.children.map((child) => child.text).join("");
    return text.length > 140 ? `${text.slice(0, 140)}...` : text;
  };

  const categories = useMemo(() => {
    const allCategories = posts.flatMap((post) =>
      post.categories?.map((cat) => cat.title) || []
    );
    return ["All", ...new Set(allCategories)];
  }, [posts]);

  const filteredPosts = useMemo(() => {
    const matchingPosts = posts.filter((post) => {
      const matchesCategory =
        activeCategory === "All" ||
        post.categories?.some((cat) => cat.title === activeCategory);

      const matchesSearch =
        post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getExcerpt(post.body).toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.author?.name?.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesCategory && matchesSearch;
    });

    return [...matchingPosts].sort((first, second) => {
      const firstDate = new Date(first.date || 0).getTime();
      const secondDate = new Date(second.date || 0).getTime();
      return dateOrder === "newest" ? secondDate - firstDate : firstDate - secondDate;
    });
  }, [posts, activeCategory, searchTerm, dateOrder]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, searchTerm, dateOrder]);

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));
  const paginatedPosts = useMemo(() => {
    const start = (currentPage - 1) * POSTS_PER_PAGE;
    return filteredPosts.slice(start, start + POSTS_PER_PAGE);
  }, [filteredPosts, currentPage]);

  const featuredPost = paginatedPosts.length > 0 ? paginatedPosts[0] : null;
  const otherPosts = paginatedPosts.slice(1);

  return (
    <>
      <SEO
        title="AI Blog | Jisedai"
        description="Explore insights, ideas, and practical perspectives on artificial intelligence, innovation, and digital transformation."
        url="https://www.jisedai.africa/blog"
      />

      <section className="relative min-h-screen overflow-hidden bg-[#eeeee8] px-5 py-28 text-[#111712] sm:px-8 md:px-12">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/4 top-10 h-64 w-64 rounded-full bg-[#bfd4c4]/45 blur-3xl" />
          <div className="absolute bottom-10 right-1/4 h-72 w-72 rounded-full bg-[#d6e85c]/30 blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="mb-12 max-w-4xl">
            <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.24em] text-[#45695a]">From the foundry</p>
            <h1 className="font-serif text-5xl leading-[0.92] tracking-[-0.06em] text-[#111712] md:text-7xl">
              Notes for people building what matters.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-[#4d5951]">
              Insights, ideas, and practical perspectives on artificial
              intelligence, innovation, and digital transformation.
            </p>
          </div>

          <div className="mb-8 grid max-w-3xl gap-3 md:grid-cols-[1fr_auto]">
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-2xl border border-[#111712]/15 bg-white/70 px-5 py-4 text-[#111712] placeholder:text-[#647169] focus:outline-none focus:border-[#45695a]/60"
            />
            <label className="sr-only" htmlFor="blog-date-order">Sort posts by date</label>
            <select
              id="blog-date-order"
              value={dateOrder}
              onChange={(event) => setDateOrder(event.target.value)}
              className="rounded-2xl border border-[#111712]/15 bg-white/70 px-5 py-4 text-sm font-semibold text-[#344039] focus:outline-none focus:border-[#45695a]/60"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
            </select>
          </div>

          <div className="mb-14 flex flex-wrap gap-3">
            {categories.map((category, index) => (
              <button
                key={index}
                onClick={() => setActiveCategory(category)}
                className={`px-5 py-2 rounded-full border transition ${
                  activeCategory === category
                    ? "border-[#111712] bg-[#111712] text-[#f5f5ef]"
                    : "border-[#111712]/15 bg-white/40 text-[#45695a] hover:border-[#45695a]/60"
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {isLoading ? (
            <p className="text-[#4d5951]">Loading posts...</p>
          ) : errorMessage ? (
            <div className="mx-auto max-w-3xl rounded-3xl border border-red-500/20 bg-red-500/10 px-6 py-5 text-center text-red-100">
              <p className="font-semibold">Blog posts could not be loaded.</p>
              <p className="mt-2 text-sm text-red-100/80">{errorMessage}</p>
            </div>
          ) : !posts.length ? (
            <div className="max-w-3xl rounded-3xl border border-[#111712]/15 bg-white/50 px-6 py-5 text-[#4d5951]">
              <p className="font-semibold text-[#45695a]">
                No published blog posts were found.
              </p>
              <p className="mt-2 text-sm text-[#4d5951]">
                If you already created posts in Sanity, make sure they are
                published and saved in the `production` dataset.
              </p>
            </div>
          ) : !filteredPosts.length ? (
            <p className="text-[#4d5951]">No matching posts found.</p>
          ) : (
            <>
              {featuredPost && (
                <div className="mb-16">
                  <Link
                    to={`/blog/${featuredPost.slug?.current}`}
                    className="group grid overflow-hidden rounded-3xl border border-[#111712]/15 bg-[#f7f7f2] transition duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#45695a]/15 lg:grid-cols-2"
                  >
                    <div className="h-[280px] overflow-hidden bg-[#bfd4c4] lg:h-full">
                      {featuredPost.mainImage?.asset?.url ? (
                        <img
                          loading="lazy"
                          src={featuredPost.mainImage.asset.url}
                          alt={featuredPost.title}
                          className="w-full h-full object-cover transition duration-700 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center font-serif text-2xl text-[#45695a]">
                          Jisedai Featured Story
                        </div>
                      )}
                    </div>

                    <div className="p-8 md:p-10 flex flex-col justify-center text-left">
                      <span className="mb-5 inline-block w-fit rounded-full border border-[#45695a]/20 bg-[#bfd4c4]/50 px-4 py-1 text-sm font-medium text-[#45695a]">
                        Featured
                      </span>

                      {featuredPost.author?.name && (
                        <p className="mb-2 text-sm text-[#667269]">
                          {featuredPost.author.name}
                        </p>
                      )}

                      {featuredPost.date && (
                        <p className="mb-3 text-sm text-[#667269]">
                          {new Date(
                            featuredPost.date
                          ).toLocaleDateString()}
                        </p>
                      )}

                      <h2 className="mb-5 font-serif text-4xl leading-tight tracking-[-0.045em] text-[#111712] md:text-5xl">
                        {featuredPost.title}
                      </h2>

                      <div className="flex flex-wrap gap-2 mb-5">
                        {featuredPost.categories?.map((cat, i) => (
                          <span
                            key={i}
                            className="rounded-full border border-[#111712]/10 bg-[#eeeee8] px-3 py-1 text-xs text-[#45695a]"
                          >
                            {cat.title}
                          </span>
                        ))}
                      </div>

                      <p className="mb-6 text-base leading-7 text-[#4d5951] md:text-lg">
                        {getExcerpt(featuredPost.body)}
                      </p>

                      <span className="inline-flex items-center gap-2 font-bold text-[#45695a] transition group-hover:text-[#111712]">
                        Read Featured Story
                        <span aria-hidden="true">&rarr;</span>
                      </span>
                    </div>
                  </Link>
                </div>
              )}

              {otherPosts.length > 0 && (
                <div>
                  <h3 className="mb-8 font-serif text-4xl tracking-[-0.04em] text-[#111712]">
                    Latest Articles
                  </h3>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {otherPosts.map((post, index) => (
                      <div
                        key={index}
                        className="flex flex-col overflow-hidden rounded-2xl border border-[#111712]/15 bg-[#f7f7f2] transition duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#45695a]/10"
                      >
                        <div className="h-52 w-full bg-[#bfd4c4]">
                          {post.mainImage?.asset?.url ? (
                            <img
                              loading="lazy"
                              src={post.mainImage.asset.url}
                              alt={post.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center font-serif text-xl text-[#45695a]">
                              Jisedai
                            </div>
                          )}
                        </div>

                        <div className="p-6 flex flex-col flex-1 text-left">
                          {post.author?.name && (
                            <p className="mb-2 text-sm text-[#667269]">
                              {post.author.name}
                            </p>
                          )}

                          {post.date && (
                            <p className="mb-3 text-sm text-[#667269]">
                              {new Date(post.date).toLocaleDateString()}
                            </p>
                          )}

                          <h2 className="mb-3 font-serif text-2xl tracking-[-0.035em] text-[#111712]">
                            {post.title}
                          </h2>

                          <div className="flex flex-wrap gap-2 mb-4">
                            {post.categories?.map((cat, i) => (
                              <span
                                key={i}
                                className="rounded-full border border-[#111712]/10 bg-[#eeeee8] px-3 py-1 text-xs text-[#45695a]"
                              >
                                {cat.title}
                              </span>
                            ))}
                          </div>

                          <p className="mb-6 flex-1 text-sm leading-7 text-[#4d5951]">
                            {getExcerpt(post.body)}
                          </p>

                          {post.slug?.current && (
                            <Link
                              to={`/blog/${post.slug.current}`}
                              className="inline-flex items-center gap-2 font-bold text-[#45695a] hover:text-[#111712]"
                            >
                              Read More
                              <span aria-hidden="true">&rarr;</span>
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {totalPages > 1 && (
                <div className="mt-14 flex flex-wrap justify-center gap-3">
                  {Array.from({ length: totalPages }, (_, index) => {
                    const page = index + 1;
                    return (
                      <button
                        key={page}
                        type="button"
                        onClick={() => setCurrentPage(page)}
                        className={`h-11 min-w-11 rounded-full border px-4 text-sm font-semibold transition ${
                          currentPage === page
                            ? "border-[#111712] bg-[#111712] text-[#f5f5ef]"
                            : "border-[#111712]/15 text-[#45695a] hover:border-[#45695a]/60"
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
}
